import { create } from 'zustand';
import { Map } from 'ol';
import View from 'ol/View';
import { fromLonLat } from 'ol/proj';

export type BasemapType = 'road' | 'satellite' | 'hybrid' | 'terrain' | 'gray';
type MapTool =
  | 'pan' | 'info' | 'drawPolygon' | 'drawPoint' | 'measure'
  | 'streetView' | 'zoomInArea' | 'zoomOutArea'
  | 'areaAnalysis' | 'cannibalization' | 'catchment' | 'ringBuffer' | 'polyInfo'
  | 'editStyle' | 'addPolygon' | 'addLine' | 'distance' | 'multiInfo' | 'addPoint' | 'filter' | 'geocoder'
  | null;

/** Where a company's map opens (Admin → Companies), in EPSG:4326. */
export interface HomeView {
  center: [number, number]; // [lon, lat]
  zoom: number;
}

interface MapState {
  mapInstance: Map | null;
  /** The company's own start view, when it has one; null = the default. */
  homeView: HomeView | null;
  view: View | null;
  zoom: number;
  center: [number, number];
  basemapType: BasemapType;
  currentTool: MapTool;
  isLoading: boolean;

  // Actions
  setMapInstance: (map: Map) => void;
  setMap: (map: Map) => void; // alias for setMapInstance
  setView: (view: View) => void;
  setZoom: (zoom: number) => void;
  setCenter: (center: [number, number]) => void;
  setBasemap: (type: BasemapType) => void;
  setCurrentTool: (tool: MapTool) => void;
  setLoading: (loading: boolean) => void;
  resetMap: () => void;
  setHomeView: (home: HomeView | null) => void;
  /** Back to the company's start view (or the default). */
  goHome: () => void;
}

export const DEFAULT_CENTER: [number, number] = [29.0, 41.0]; // Istanbul region
export const DEFAULT_ZOOM = 11;

export const useMapStore = create<MapState>((set) => ({
  mapInstance: null,
  homeView: null,
  view: null,
  zoom: DEFAULT_ZOOM,
  center: DEFAULT_CENTER,
  basemapType: 'road',
  currentTool: 'pan',
  isLoading: true,

  setMapInstance: (map: Map) => {
    const view = map.getView();
    set({ mapInstance: map, view, isLoading: false });
  },

  // Alias — components can use either setMap or setMapInstance
  setMap: (map: Map) => {
    const view = map.getView();
    set({ mapInstance: map, view, isLoading: false });
  },

  setView: (view: View) => {
    set({ view });
    const center = view.getCenter() as [number, number];
    const zoom = view.getZoom() || DEFAULT_ZOOM;
    set({ center, zoom });
  },

  setZoom: (zoom: number) => {
    set({ zoom });
    const state = useMapStore.getState();
    // Only push a zoom that differs from the view's. Writing the view's own zoom back while it animates
    // (view.fit / animate with a duration) cancels the animation in OpenLayers: that is what stopped the
    // address search from zooming to the chosen place.
    const view = state.view;
    if (view && !view.getAnimating() && Math.abs((view.getZoom() ?? zoom) - zoom) > 1e-6) {
      view.setZoom(zoom);
    }
  },

  setCenter: (center: [number, number]) => {
    set({ center });
    const state = useMapStore.getState();
    if (state.view) {
      // center is [lon, lat] in EPSG:4326 — must transform to EPSG:3857 for the OL view
      state.view.setCenter(fromLonLat(center, 'EPSG:3857'));
    }
  },

  setBasemap: (type: BasemapType) => {
    set({ basemapType: type });
  },

  setCurrentTool: (tool: MapTool) => {
    set({ currentTool: tool });
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setHomeView: (home: HomeView | null) => {
    set({ homeView: home });
  },

  goHome: () => {
    const { view, homeView } = useMapStore.getState();
    if (!view) return;
    const home = homeView ?? { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM };
    view.cancelAnimations();
    view.animate({ center: fromLonLat(home.center, 'EPSG:3857'), zoom: home.zoom, duration: 500 });
  },

  resetMap: () => {
    set({
      mapInstance: null,
      view: null,
      zoom: DEFAULT_ZOOM,
      center: DEFAULT_CENTER,
      basemapType: 'road',
      currentTool: 'pan',
      isLoading: true,
    });
  },
}));

export default useMapStore;
